import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
  TextField,
  Button,
  Stack,
  Container,
  MenuItem,
  Paper,
  Box,
  Typography,
  Grid,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import * as Yup from 'yup';
import {
  ExpandMore,
  MailOutline,
  LockOutlined,
  CompareArrows,
  ScheduleOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { autocomplete } from '../api/services/autocomplete';
import VehicleDetailsStep from '../components/VehicleDetailsStep';

const MILEAGE_OPTIONS = Array.from({ length: 21 }, (_, i) => i * 10000);
const CURRENT_YEAR = new Date().getFullYear();

const yearOptions = (carType: string) =>
  carType === 'new'
    ? Array.from({ length: 3 }, (_, i) => CURRENT_YEAR + 1 - i)
    : Array.from({ length: CURRENT_YEAR - 1999 }, (_, i) => CURRENT_YEAR - i);

const INK = '#101828';
const PAPER = '#F4F5F2';
const AMBER = '#F2B33D';

/** The email dealers actually receive — shown verbatim so buyers know what goes out under their name. */
const SampleEmail = () => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2.5, sm: 3 },
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: '#fff',
      fontFamily: '"IBM Plex Mono", monospace',
      fontSize: 13.5,
      lineHeight: 1.6,
      color: INK,
    }}
  >
    <Box sx={{ color: 'text.secondary', mb: 1.5, fontSize: 12 }}>
      From: Trey M. &lt;deal-a41c…@mail.dealerscout…&gt;
      <br />
      Subject: OTD price request — 2023 Toyota Tacoma TRD Sport (VIN 3TYCZ5AN…)
    </Box>
    <Divider sx={{ mb: 1.5 }} />
    Hi Serra Toyota,
    <br />
    <br />
    I&apos;m looking at your 2023 Toyota Tacoma TRD Sport, VIN 3TYCZ5AN…, listed
    at $38,095.
    <br />
    <br />
    Could you reply by email with your best out-the-door price — the total
    including tax, title, doc fee, and any dealer add-ons, itemized?
    <br />
    <br />
    <Box
      component="span"
      sx={{ bgcolor: '#FFF3D6', px: 0.5, borderRadius: 0.5 }}
    >
      For reference, I&apos;m looking at a 2023 Toyota Tacoma TRD Sport with
      37,209 miles listed at $37,975 about 77 miles from you, and another with
      49,999 miles at $38,229.
    </Box>
    <br />
    <br />
    I&apos;m a serious buyer and can pay cash or arrange my own financing.
    <br />
    <br />
    Thanks,
    <br />
    Trey M.
  </Paper>
);

export default function Landing() {
  const navigate = useNavigate();
  const [makes, setMakes] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [trims, setTrims] = useState<string[]>([]);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [locationDenied, setLocationDenied] = useState(false);
  const [inventoryError, setInventoryError] = useState<string | null>(null);

  const validationSchema = Yup.object({
    year: Yup.string().required('Year is required'),
    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    trim: Yup.string(),
    car_type: Yup.string().required('Condition is required'),
    minMileage: Yup.string(),
    maxMileage: Yup.string().test(
      'is-greater',
      'Max mileage must be greater than min mileage',
      function (value) {
        if (this.parent.car_type !== 'used') return true;
        return Number(value) > Number(this.parent.minMileage);
      }
    ),
    radius: Yup.string().required('Search radius is required'),
    zip: Yup.string().test(
      'zip-or-location',
      'Enter a ZIP code so we can search near you',
      function (value) {
        if (location) return true;
        return /^\d{5}$/.test(value ?? '');
      }
    ),
  });

  const formik = useFormik({
    initialValues: {
      year: CURRENT_YEAR.toString(),
      make: '',
      model: '',
      trim: '',
      car_type: 'used',
      minMileage: '0',
      maxMileage: '50000',
      radius: '50',
      zip: '',
    },
    validationSchema,
    onSubmit: (values) => {
      const params = new URLSearchParams({
        year: values.year,
        make: values.make,
        model: values.model,
        trim: values.trim,
        car_type: values.car_type,
        radius: values.radius,
      });
      if (values.car_type === 'used') {
        params.set('miles_range', `${values.minMileage}-${values.maxMileage}`);
      }
      if (location) {
        params.set('latitude', location.lat.toString());
        params.set('longitude', location.lng.toString());
      } else {
        params.set('zip', values.zip);
      }
      navigate(`/cars?${params}`);
    },
  });

  const { year, car_type: carType, make, model } = formik.values;
  const { setFieldValue } = formik;

  useEffect(() => {
    let cancelled = false;
    autocomplete({ field: 'make', input: '', year: Number(year) })
      .then((res) => {
        if (cancelled) return;
        setMakes(res.terms ?? []);
        setInventoryError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setMakes([]);
        setInventoryError(
          e?.response?.data?.message ||
            'Inventory search is temporarily unavailable. Please try again later.'
        );
      });
    setModels([]);
    setTrims([]);
    setFieldValue('make', '');
    setFieldValue('model', '');
    setFieldValue('trim', '');
    return () => {
      cancelled = true;
    };
  }, [year, carType, setFieldValue]);

  useEffect(() => {
    if (!make) return undefined;
    let cancelled = false;
    autocomplete({ field: 'model', input: '', make, year: Number(year) })
      .then((res) => !cancelled && setModels(res.terms ?? []))
      .catch(() => !cancelled && setModels([]));
    setTrims([]);
    setFieldValue('trim', '');
    return () => {
      cancelled = true;
    };
  }, [make, year, setFieldValue]);

  useEffect(() => {
    if (!model) return undefined;
    let cancelled = false;
    autocomplete({ field: 'trim', input: '', make, model, year: Number(year) })
      .then((res) => !cancelled && setTrims(res.terms ?? []))
      .catch(() => !cancelled && setTrims([]));
    return () => {
      cancelled = true;
    };
  }, [model, make, year]);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setLocationDenied(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }),
      () => setLocationDenied(true),
      { timeout: 8000 }
    );
  }, []);

  const steps = [
    {
      icon: <CompareArrows />,
      title: 'Pick up to five listings',
      body: 'Search live dealer inventory near you. We flag high-miles-for-age trucks and one-owner cars so you skip the rental-fleet bargains.',
    },
    {
      icon: <MailOutline />,
      title: 'We email each dealer as you',
      body: 'A plain, itemized out-the-door price request goes out from a private alias — with two or three cheaper comparable listings cited by the numbers. Follow-ups happen automatically.',
    },
    {
      icon: <ScheduleOutlined />,
      title: 'Replies land on one page',
      body: 'Written quotes are ranked on total value: price, mileage, and any extras. After 48 hours, dealers who are behind get asked to beat the best number.',
    },
    {
      icon: <LockOutlined />,
      title: 'Accept, and only then connect',
      body: 'Your name and phone go to one dealer — the one you chose. Decline the rest with one click and a polite email goes out for you.',
    },
  ];

  const faqs = [
    {
      q: 'Is it really free?',
      a: "Yes. No dealer pays us and we don't sell your information. If we ever add paid options they'll be for buyers, clearly labeled, and optional.",
    },
    {
      q: 'Will dealers call me?',
      a: 'They can\'t — they never get your number. Requests go out from a per-deal email alias, and replies come back to your deal page. Some dealers reply "come on in"; we count that as no written quote and follow up once.',
    },
    {
      q: "What's an out-the-door price?",
      a: "The total you'd pay to drive off: vehicle price plus tax, title, doc fee, and every add-on. It's the only number worth comparing across dealers, which is why we ask for it itemized.",
    },
    {
      q: 'How long does it take?',
      a: 'Requests go out immediately. Most written replies arrive within a day; the request window closes after three days and unanswered dealers are marked as such.',
    },
  ];

  return (
    <Box sx={{ color: INK }}>
      {/* Hero */}
      <Container
        maxWidth="lg"
        sx={{ pt: { xs: 2, md: 6 }, pb: { xs: 6, md: 10 } }}
      >
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Chip
              label="Free for buyers · No dealer pays us"
              size="small"
              sx={{
                bgcolor: '#FFF3D6',
                color: '#6B4E00',
                fontWeight: 600,
                mb: 2,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: 36, sm: 44, md: 52 },
                lineHeight: 1.05,
                mb: 2,
                textWrap: 'balance',
              }}
            >
              Written out-the-door prices from five dealers. Zero phone calls.
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                fontWeight: 400,
                color: '#475467',
                mb: 3,
                maxWidth: 520,
                fontFamily: '"IBM Plex Sans", sans-serif',
              }}
            >
              Choose the exact vehicles you want. DealerScout asks each dealer
              for an itemized price under a private alias, cites real comparable
              listings, and ranks what comes back — so you negotiate from
              numbers, not a showroom chair.
            </Typography>
            <Stack spacing={1.25} sx={{ mb: 3 }}>
              {[
                'Dealers never see your name or number until you accept',
                'Comparable listings cited automatically, by price and miles',
                'Follow-ups and counter-requests handled for you',
              ].map((line) => (
                <Stack
                  key={line}
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                >
                  <CheckCircleOutline
                    sx={{ color: 'success.main', fontSize: 22, mt: 0.2 }}
                  />
                  <Typography>{line}</Typography>
                </Stack>
              ))}
            </Stack>
            <Typography variant="caption" sx={{ color: '#667085' }}>
              Inventory from live dealer listings within 100 miles. Currently US
              only.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 4 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                boxShadow: '0 24px 48px -24px rgba(16,24,40,0.25)',
              }}
            >
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                What are you shopping for?
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
                Pick a year, make, model and trim. You&apos;ll choose specific
                listings next.
              </Typography>
              <form onSubmit={formik.handleSubmit}>
                <VehicleDetailsStep
                  formik={formik}
                  makes={makes}
                  models={models}
                  trims={trims}
                  yearOptions={yearOptions(carType)}
                  mileageOptions={MILEAGE_OPTIONS}
                />
                <Stack spacing={2} sx={{ mt: 3 }}>
                  {inventoryError && (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      {inventoryError}
                    </Alert>
                  )}
                  {locationDenied && (
                    <Alert severity="info" sx={{ py: 0 }}>
                      Location is off — enter a ZIP code and we&apos;ll search
                      near it.
                    </Alert>
                  )}
                  {(locationDenied || !location) && (
                    <TextField
                      fullWidth
                      id="zip"
                      name="zip"
                      label="ZIP code"
                      inputProps={{ inputMode: 'numeric', maxLength: 5 }}
                      value={formik.values.zip}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.zip && Boolean(formik.errors.zip)}
                      helperText={formik.touched.zip && formik.errors.zip}
                    />
                  )}
                  <TextField
                    select
                    fullWidth
                    id="radius"
                    name="radius"
                    label="How far will you travel?"
                    value={formik.values.radius}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.radius && Boolean(formik.errors.radius)
                    }
                    helperText={formik.touched.radius && formik.errors.radius}
                  >
                    {[25, 50, 100].map((distance) => (
                      <MenuItem key={distance} value={distance.toString()}>
                        {distance} miles
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    type="submit"
                    size="large"
                    disabled={formik.isSubmitting}
                    sx={{
                      py: 1.5,
                      bgcolor: INK,
                      '&:hover': { bgcolor: '#1D2939' },
                    }}
                  >
                    Find listings
                  </Button>
                </Stack>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* How it works */}
      <Box sx={{ bgcolor: PAPER, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography
                variant="overline"
                sx={{ color: '#667085', letterSpacing: '0.12em' }}
              >
                How it works
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 30, md: 38 },
                  mb: 4,
                  textWrap: 'balance',
                }}
              >
                The email a dealer actually gets
              </Typography>
              <Stack spacing={3}>
                {steps.map((step) => (
                  <Stack
                    key={step.title}
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#fff',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        color: INK,
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475467' }}>
                        {step.body}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <SampleEmail />
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1.5, color: '#667085' }}
              >
                Real request from a recent deal. The highlighted line is
                generated from live listings — only ones priced below the
                dealer&apos;s, never the same dealer, never named.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What you get / what we're not */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={7}>
            <Typography
              variant="h2"
              sx={{ fontSize: { xs: 30, md: 38 }, mb: 3, textWrap: 'balance' }}
            >
              Compare the number that matters
            </Typography>
            <Typography sx={{ color: '#475467', mb: 3, maxWidth: 560 }}>
              Sticker prices hide $800 doc fees and $1,995 &quot;market
              adjustments.&quot; Every quote on your deal page is the itemized
              out-the-door total, scored on price, mileage, and included extras
              — so the cheapest sticker doesn&apos;t win by accident.
            </Typography>
            <Grid container spacing={2}>
              {[
                [
                  'Sealed for 24 hours',
                  'Dealers quote blind first, then see anonymized competition and can improve.',
                ],
                [
                  'Three-day window',
                  'Long enough for real replies, short enough that nobody strings you along.',
                ],
                [
                  'Fleet-risk flags',
                  'High-miles-for-age listings are marked before you request anything.',
                ],
                [
                  'Your contact, your call',
                  'Accepting an offer is the only thing that shares your details.',
                ],
              ].map(([title, body]) => (
                <Grid item xs={12} sm={6} key={title}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      height: '100%',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 0.5 }}
                    >
                      {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475467' }}>
                      {body}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: 2, bgcolor: INK, color: '#fff' }}
            >
              <Typography variant="h5" sx={{ mb: 1.5, color: AMBER }}>
                What DealerScout is not
              </Typography>
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  <strong>Not a lead site.</strong> Quote-request sites sell
                  your phone number to every dealer in range. We send one email,
                  from an alias, and no dealer pays us.
                </Typography>
                <Typography variant="body2">
                  <strong>Not a &quot;guaranteed price.&quot;</strong> Dealers
                  quote what they quote. We make it written, itemized, and
                  comparable — and give you the leverage to push.
                </Typography>
                <Typography variant="body2">
                  <strong>Not a broker.</strong> You buy from the dealer
                  directly. We&apos;re out of the picture the moment you accept.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* FAQ */}
      <Box sx={{ bgcolor: PAPER, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontSize: { xs: 30, md: 38 }, mb: 3 }}>
            Questions
          </Typography>
          {faqs.map((f) => (
            <Accordion
              key={f.q}
              elevation={0}
              disableGutters
              sx={{
                bgcolor: 'transparent',
                '&:before': { display: 'none' },
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontWeight: 600 }}>{f.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" sx={{ color: '#475467' }}>
                  {f.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            sx={{ mt: 5 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{ bgcolor: INK, '&:hover': { bgcolor: '#1D2939' } }}
            >
              Start a search
            </Button>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Takes a minute. No account needed until you send.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
